import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useGoalkeeperProfile,
  usePlayerDevelopment,
  useSaveGoalkeeperProfile,
  useTrialHistory,
} from '@/app/queries';
import { ageLabel, formatBirthDate, formatDate, sheetName } from '@/lib/format';
import { configLabel } from '@/lib/labels';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/ui/Field';
import { EmptyState, Notice, PageHeader, Panel } from '@/ui/Panel';
import { Chip, PositionChip } from '@/ui/Chip';
import { Detail } from '@/ui/Section';
import { unitOf } from '@shared/formations';

const DIMENSIONS = ['technical', 'tactical', 'physical', 'mental', 'social', 'goalkeeping'] as const;

/**
 * One dimension's ratings over time, as a bar per assessment.
 *
 * Not a line: the readings are occasions, not a continuous signal, and drawing
 * them joined would imply the player was measured on the days between.
 */
function DimensionTrack({
  values,
  max,
}: {
  values: { onDate: string; value: number | undefined }[];
  max: number;
}) {
  return (
    <span className="flex h-8 items-end gap-0.5" aria-hidden="true">
      {values.map((v) => (
        <span
          key={v.onDate}
          className={`w-2 rounded-t-[1px] ${v.value === undefined ? 'bg-line' : 'bg-ink-muted'}`}
          style={{ height: v.value === undefined ? '3px' : `${Math.max(8, (v.value / max) * 100)}%` }}
        />
      ))}
    </span>
  );
}

/**
 * The keeper's own panel.
 *
 * Height and weight sit with every other player; reach and span do not, because
 * they are the measurements a keeper is actually recruited on and they mean
 * nothing for anyone else.
 */
function GoalkeeperPanel({ playerId }: { playerId: string }) {
  const t = useTranslations();
  const profile = useGoalkeeperProfile(playerId);
  const save = useSaveGoalkeeperProfile(playerId);

  const [reach, setReach] = useState('');
  const [span, setSpan] = useState('');
  const [foot, setFoot] = useState('');
  const [hand, setHand] = useState('');
  const [note, setNote] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const row = profile.data;
    setReach(row?.standingReachCm != null ? String(row.standingReachCm) : '');
    setSpan(row?.wingspanCm != null ? String(row.wingspanCm) : '');
    setFoot(row?.distributionFoot ?? '');
    setHand(row?.dominantHand ?? '');
    setNote(row?.profileNote ?? '');
    setDirty(false);
  }, [profile.data]);

  const number = (v: string) => (v.trim() === '' ? null : Number(v));

  return (
    <Panel
      title={t('goalkeeper.title')}
      count={t('goalkeeper.lead')}
      action={
        <Button
          variant="primary"
          busy={save.isPending}
          disabled={!dirty}
          onClick={() =>
            void save
              .mutateAsync({
                standingReachCm: number(reach),
                wingspanCm: number(span),
                distributionFoot: foot || null,
                dominantHand: hand || null,
                profileNote: note || null,
              })
              .then(() => setDirty(false))
          }
        >
          {t('goalkeeper.save')}
        </Button>
      }
    >
      <div className="grid gap-x-5 gap-y-2 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t('goalkeeper.standingReach')} optional>
          {(p) => (
            <TextInput
              {...p}
              type="number"
              inputMode="numeric"
              value={reach}
              onChange={(e) => {
                setReach(e.target.value);
                setDirty(true);
              }}
            />
          )}
        </Field>
        <Field label={t('goalkeeper.wingspan')} optional>
          {(p) => (
            <TextInput
              {...p}
              type="number"
              inputMode="numeric"
              value={span}
              onChange={(e) => {
                setSpan(e.target.value);
                setDirty(true);
              }}
            />
          )}
        </Field>
        <Field label={t('goalkeeper.distributionFoot')} optional>
          {(p) => (
            <SelectInput
              {...p}
              value={foot}
              onChange={(e) => {
                setFoot(e.target.value);
                setDirty(true);
              }}
            >
              <option value="">{t('goalkeeper.unset')}</option>
              <option value="left">{t('goalkeeper.left')}</option>
              <option value="right">{t('goalkeeper.right')}</option>
              <option value="both">{t('goalkeeper.both')}</option>
            </SelectInput>
          )}
        </Field>
        <Field label={t('goalkeeper.dominantHand')} optional>
          {(p) => (
            <SelectInput
              {...p}
              value={hand}
              onChange={(e) => {
                setHand(e.target.value);
                setDirty(true);
              }}
            >
              <option value="">{t('goalkeeper.unset')}</option>
              <option value="left">{t('goalkeeper.left')}</option>
              <option value="right">{t('goalkeeper.right')}</option>
            </SelectInput>
          )}
        </Field>
        <div className="sm:col-span-2 lg:col-span-4">
          <Field label={t('goalkeeper.profileNote')} optional>
            {(p) => (
              <TextArea
                {...p}
                rows={2}
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  setDirty(true);
                }}
              />
            )}
          </Field>
        </div>
      </div>
    </Panel>
  );
}

export function PlayerFile({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const { playerId } = useParams<{ playerId: string }>();
  const file = usePlayerDevelopment(playerId ?? null);
  const trials = useTrialHistory(playerId ?? null);

  if (file.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  if (file.isError || !file.data?.player) {
    return <Notice tone="risk">{t('error.generic')}</Notice>;
  }

  const { player, evaluations, testing, readinessSteps, latestReadiness } = file.data;
  const age = ageLabel(player.dateOfBirth);
  // Every scale seen in this player's history; the tallest governs the bars so
  // an org that moved from 1–5 to 1–10 does not get a cliff in the chart.
  const scaleMax = Math.max(
    5,
    ...evaluations.flatMap((e) => Object.values(e.averages)),
  );

  return (
    <>
      <div className="mb-4">
        <Link
          to={localePath(locale, '/development')}
          className="text-meta text-ink-faint underline-offset-2 hover:text-ink hover:underline"
        >
          ← {t('development.back')}
        </Link>
      </div>

      <PageHeader
        title={sheetName(player.knownAs ?? player.firstName, player.lastName)}
        lead={t('development.fileLead')}
      />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        {player.positionKey ? (
          <PositionChip code={player.positionKey} unit={unitOf(player.positionKey)} />
        ) : null}
        {player.secondaryPositionKey ? (
          <PositionChip
            code={player.secondaryPositionKey}
            unit={unitOf(player.secondaryPositionKey)}
          />
        ) : null}
        {age ? <Chip muted>{age}</Chip> : null}
        {player.dateOfBirth ? (
          <span className="tabular text-meta text-ink-faint">
            {formatBirthDate(player.dateOfBirth, locale)}
          </span>
        ) : null}
        <Chip>
          {t('development.readiness')}:{' '}
          {latestReadiness
            ? t(`development.readinessValue.${latestReadiness}`)
            : t('development.readinessValue.unset')}
        </Chip>
      </div>

      {evaluations.length === 0 && testing.length === 0 ? (
        <Panel title={t('development.file')}>
          <EmptyState title={t('development.noHistory')} body={t('development.noHistoryBody')} />
        </Panel>
      ) : null}

      <div className="grid gap-5">
        {player.positionKey === 'GK' || player.secondaryPositionKey === 'GK' ? (
          <GoalkeeperPanel playerId={player.id} />
        ) : null}

        {(trials.data ?? []).length > 0 ? (
          <Panel title={t('trial.history')} count={(trials.data ?? []).length}>
            <ul className="divide-y divide-line">
              {(trials.data ?? []).map((row) => (
                <li key={row.candidateId} className="flex flex-wrap items-baseline gap-x-3 px-4 py-2">
                  <span className="tabular w-24 shrink-0 text-meta text-ink-muted">
                    {formatDate(row.startsOn, locale)}
                  </span>
                  <span className="font-medium">{row.trialName}</span>
                  {row.decisionReason ? (
                    <span className="text-meta text-ink-muted">{row.decisionReason}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </Panel>
        ) : null}

        {evaluations.length > 0 ? (
          <Panel title={t('evaluation.title')} explain="senior_readiness" count={evaluations.length}>
            <div className="overflow-x-auto px-4 py-4">
              {/* No floor: three columns, one of which is a track that can
                  simply be shorter. 544px was giving the track room a phone
                  does not have, at the cost of the whole page scrolling. */}
              <table className="w-full table-fixed border-collapse">
                <caption className="sr-only">{t('evaluation.average')}</caption>
                <tbody className="divide-y divide-line">
                  {DIMENSIONS.map((dimension) => {
                    const values = evaluations.map((e) => ({
                      onDate: e.onDate,
                      value: e.averages[dimension],
                    }));
                    if (values.every((v) => v.value === undefined)) return null;
                    const last = [...values].reverse().find((v) => v.value !== undefined);
                    return (
                      <tr key={dimension}>
                        <th
                          scope="row"
                          className="w-20 py-2 pe-3 text-start text-meta font-normal text-ink-muted sm:w-28"
                        >
                          {t(`development.dimension.${dimension}`)}
                        </th>
                        <td className="w-full py-2">
                          <DimensionTrack values={values} max={scaleMax} />
                        </td>
                        <td className="tabular w-14 py-2 text-end font-condensed text-subhead font-semibold sm:w-16">
                          {last?.value?.toFixed(1) ?? '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <ul className="divide-y divide-line border-t border-line">
              {[...evaluations].reverse().map((e) => (
                <li key={e.id} className="px-4 py-3">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="tabular font-condensed text-subhead font-semibold">
                      {formatDate(e.onDate, locale)}
                    </span>
                    {e.context ? (
                      <span className="text-meta text-ink-muted">{e.context}</span>
                    ) : null}
                    <span className="tabular ms-auto text-meta text-ink-faint">
                      {t('evaluation.complete', { percent: Math.round(e.completeness * 100) })}
                    </span>
                  </div>
                  {e.summary ? <p className="mt-1 max-w-[70ch]">{e.summary}</p> : null}
                  <div className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2">
                    {e.strengths ? (
                      <Detail label={t('evaluation.strengths')}>{e.strengths}</Detail>
                    ) : null}
                    {e.weaknesses ? (
                      <Detail label={t('evaluation.weaknesses')}>{e.weaknesses}</Detail>
                    ) : null}
                  </div>
                  {e.seniorReadiness ? (
                    <p className="mt-2 text-meta text-ink-muted">
                      {t('development.readiness')}:{' '}
                      {t(`development.readinessValue.${e.seniorReadiness}`)}
                      {e.seniorReadinessNote ? ` — ${e.seniorReadinessNote}` : ''}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          </Panel>
        ) : null}

        {readinessSteps.length > 0 ? (
          <Panel title={t('development.readiness')} count={readinessSteps.length}>
            <ul className="divide-y divide-line">
              {[...readinessSteps].reverse().map((step) => (
                <li key={step.onDate} className="flex items-center gap-3 px-4 py-2">
                  <span className="tabular w-24 shrink-0 text-meta text-ink-muted">
                    {formatDate(step.onDate, locale)}
                  </span>
                  <span className={step.change > 0 ? 'text-ok' : 'text-risk'}>
                    {step.change > 0 ? t('development.movedUp') : t('development.movedDown')}
                  </span>
                  <span className="text-meta text-ink-muted">
                    {step.to ? t(`development.readinessValue.${step.to}`) : ''}
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        ) : null}

        {testing.length > 0 ? (
          <Panel title={t('testing.title')} count={testing.length}>
            <ul className="divide-y divide-line">
              {testing.map((row) => (
                <li key={row.test.key} className="px-4 py-3">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-condensed text-subhead font-semibold">
                      {configLabel(row.test, t)}
                    </span>
                    <span className="text-meta text-ink-faint">
                      {row.test.unit ? t('testing.unitLabel', { unit: row.test.unit }) : ''}
                      {' · '}
                      {row.test.direction === 'lower'
                        ? t('testing.lowerIsBetter')
                        : t('testing.higherIsBetter')}
                    </span>
                    <span
                      className={`ms-auto text-meta ${
                        row.trend.trend === 'improving'
                          ? 'text-ok'
                          : row.trend.trend === 'declining'
                            ? 'text-risk'
                            : 'text-ink-muted'
                      }`}
                    >
                      {row.trend.trend === 'improving'
                        ? t('development.trendImproving')
                        : row.trend.trend === 'declining'
                          ? t('development.trendDeclining')
                          : row.trend.trend === 'stable'
                            ? t('development.trendStable')
                            : t('development.trendUnknown')}
                    </span>
                  </div>

                  <div className="mt-2 flex flex-wrap items-baseline gap-x-5 gap-y-1">
                    <span className="tabular">
                      <span className="text-meta text-ink-faint">{t('development.latest')} </span>
                      <span className="font-condensed text-subhead font-semibold">
                        {row.latest.value}
                      </span>
                      <span className="text-meta text-ink-faint">
                        {' '}
                        {formatDate(row.latest.onDate, locale)}
                      </span>
                    </span>
                    {row.sinceFirst ? (
                      <span
                        className={`tabular text-meta ${
                          row.sinceFirst.improved ? 'text-ok' : 'text-risk'
                        }`}
                        dir="ltr"
                      >
                        {t('development.sinceFirst')} {row.sinceFirst.improved ? '▲' : '▼'}{' '}
                        {Math.abs(row.sinceFirst.percent).toFixed(1)}%
                      </span>
                    ) : null}
                    <span className="text-meta text-ink-faint">
                      {t('development.readings', { count: row.values.length })}
                    </span>
                  </div>

                  <ol className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                    {row.values.map((v) => (
                      <li key={v.onDate} className="tabular text-meta text-ink-muted">
                        <span className="text-ink-faint">{formatDate(v.onDate, locale)}</span>{' '}
                        {v.value}
                      </li>
                    ))}
                  </ol>
                </li>
              ))}
            </ul>
          </Panel>
        ) : null}
      </div>
    </>
  );
}
